// components/TextArea.tsx
import * as React from 'react';

import { disabledBackgroundGray } from '../pages/constants';

type KeyEvent = React.KeyboardEvent<HTMLTextAreaElement>;

interface TextAreaProps {
  label?: string;
  placeholder?: string;
  value?: string;
  setTextChange?: (text: string) => void;
  disabled?: boolean;
  className?: string;
  bottomText?: string;
  error?: string;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  autoGrow?: boolean;
  monospace?: boolean;
  maxLength?: number;
  showCounter?: boolean;
  onSubmit?: () => void;
}

export const TextInputMultiLine: React.FC<TextAreaProps> = ({
  label,
  placeholder,
  value = '',
  setTextChange,
  disabled = false,
  className = '',
  bottomText = '',
  error = '',
  rows = 6,
  minRows,
  maxRows,
  autoGrow = true,
  monospace = false,
  maxLength,
  showCounter = false,
  onSubmit,
}) => {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);

  const resize = React.useCallback(() => {
    const el = ref.current;
    if (!el || !autoGrow) return;

    // Reset height to measure scrollHeight accurately
    el.style.height = 'auto';

    // Compute lineHeight to respect min/max rows if provided
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '20');
    let newHeight = el.scrollHeight;

    if (minRows) {
      const minH = minRows * lineHeight + (el.offsetHeight - el.clientHeight);
      newHeight = Math.max(newHeight, minH);
    }
    if (maxRows) {
      const maxH = maxRows * lineHeight + (el.offsetHeight - el.clientHeight);
      newHeight = Math.min(newHeight, maxH);
    }

    el.style.height = `${newHeight}px`;
  }, [autoGrow, minRows, maxRows]);

  React.useEffect(() => {
    resize();
  }, [value, resize]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!disabled && setTextChange) setTextChange(e.target.value);
  };

  const handleKeyDown = (e: KeyEvent) => {
    // Cmd/Ctrl + Enter submits (nice for ACs)
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  const base =
    'border border-gray-300 rounded text-base p-2 w-full outline-none focus:border-gray-400';
  const mono = monospace ? 'font-mono' : '';
  const disabledCls = disabled ? disabledBackgroundGray : '';

  return (
    <div className="w-full">
      {label && <div className="mb-1 text-sm text-slate-600">{label}</div>}

      <div className="relative">
        <textarea
          ref={ref}
          className={`${base} ${mono} ${disabledCls} ${className}`}
          placeholder={placeholder ?? ''}
          disabled={disabled}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={rows}
          maxLength={maxLength}
        />
        {showCounter && typeof maxLength === 'number' && (
          <span className="absolute bottom-2 right-2 text-xs text-slate-400">
            {value.length} / {maxLength}
          </span>
        )}
      </div>

      {error ? (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      ) : (
        bottomText && <p className="text-xs text-slate-500 mt-1">{bottomText}</p>
      )}
    </div>
  );
};
